// src/pages/HomePage.js

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import Button from '../components/common/Button';
import SectionTitle from '../components/common/SectionTitle';
import Card from '../components/common/Card';
import NeuralBackground from '../components/background/NeuralBackground';
import FeaturedEvent from './FeaturedEvent';
// Importation des images directement
import ddtLogo from '../assets/images/ddt.jpg';
import aeLogo from '../assets/images/aefas.png';
import awsLogo from '../assets/images/comsas.png';
import ibmLogo from '../assets/images/ae.png';
import oracleLogo from '../assets/images/africa.jpeg';
// Importation des services API
import { 
  serviceService, 
  projectService, 
  solutionService, 
  testimonialService, 
  userService,
} from '../api';

import techImagePath from '../assets/images/check1.svg'; 

// Styled components pour la page d'accueil
const HeroSection = styled.section`
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 2rem;
`;

const HeroContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CompanyName = styled(motion.div)`
  margin-bottom: 1.5rem;
  
  h1 {
    font-size: 4rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    line-height: 1.1;
    
    .blue {
      color: #56C8FF; /* Bleu ciel */
    }
    
    .orange {
      color: #FF7A00; /* Orange foncé */
    }
    
    .gold {
      color: #FFD700; /* Or */
    }
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
`;

const HeroDescription = styled(motion.p)`
  font-size: 1.4rem;
  max-width: 600px;
  margin-bottom: 2.5rem;
  color: ${props => props.theme.text};
  line-height: 1.5;
`;

const ImageContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 10px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }
`;

const Section = styled.section`
  padding: 5rem 2rem;
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const StatsSection = styled.section`
  background: ${props => props.isDark ? 'rgba(9, 14, 44, 0.8)' : 'rgba(239, 244, 255, 0.8)'};
  padding: 4rem 0;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
`;

const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
  padding: 0 2rem;
`;

const StatItem = styled(motion.div)`
  text-align: center;
`;

const StatNumber = styled.h3`
  font-size: 3.5rem;
  font-weight: bold;
  color: #56C8FF;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const PartnerSection = styled.section`
  padding: 4rem 2rem;
  background: ${props => props.theme.backgroundSecondary};
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  justify-items: center;
  align-items: center;
`;

const PartnerLogo = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  width: 100%;
  box-shadow: ${props => props.theme.card.shadow};
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const SolutionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const SolutionCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${props => props.theme.card.shadow};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
  
  .image-container {
    height: 180px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
  }
  
  &:hover .image-container img {
    transform: scale(1.05);
  }
  
  .content {
    padding: 1.5rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    
    h3 {
      font-size: 1.3rem;
      margin-bottom: 0.75rem;
      color: #56C8FF;
    }
    
    p {
      color: ${props => props.theme.text};
      margin-bottom: 1rem;
      flex-grow: 1;
    }
    
    .technologies {
      color: ${props => props.theme.textSecondary};
      font-size: 0.9rem;
      margin-top: auto;
    }
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const TeamMemberCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${props => props.theme.card.shadow};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  .image-container {
    height: 250px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  
  .content {
    padding: 1.5rem;
    text-align: center;
    
    h3 {
      font-size: 1.2rem;
      margin-bottom: 0.5rem;
      color: #56C8FF;
    }
    
    p {
      color: ${props => props.theme.textSecondary};
      font-size: 0.9rem;
    }
    
    .social-links {
      display: flex;
      justify-content: center;
      margin-top: 1rem;
      gap: 0.75rem;
      
      a {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: ${props => props.theme.backgroundSecondary};
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${props => props.theme.text};
        transition: all 0.3s ease;
        
        &:hover {
          background: #56C8FF;
          color: white;
        }
      }
    }
  }
`;

const TestimonialCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.theme.card.shadow};
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .quote {
    font-size: 1.1rem;
    line-height: 1.6;
    font-style: italic;
    color: ${props => props.theme.text};
    margin-bottom: 1.5rem;
    flex-grow: 1;
    position: relative;
    
    &:before {
      content: '"';
      font-size: 4rem;
      position: absolute;
      top: -1.5rem;
      left: -0.5rem;
      color: rgba(86, 200, 255, 0.2);
      font-family: serif;
    }
  }
  
  .author {
    display: flex;
    align-items: center;
    
    .author-image {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      overflow: hidden;
      margin-right: 1rem;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    
    .author-info {
      h4 {
        font-size: 1rem;
        color: #56C8FF;
        margin-bottom: 0.2rem;
      }
      
      p {
        font-size: 0.85rem;
        color: ${props => props.theme.textSecondary};
      }
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  
  .spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border-left-color: #56C8FF;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const GradientButton = styled(Button)`
  background: linear-gradient(90deg, #56C8FF 0%, #FF7A00 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  
  &:hover {
    background: linear-gradient(90deg, #FF7A00 0%, #56C8FF 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }
`;

// Partenaires avec les images importées
const partners = [
  { id: 1, name: "DIGITAL DREAM TEAM", logo: ddtLogo },
  { id: 2, name: "A2ELBM2", logo: aeLogo },
  { id: 3, name: "AWS", logo: awsLogo },
  { id: 4, name: "IBM", logo: ibmLogo },
  { id: 5, name: "Oracle", logo: oracleLogo },
];

const HomePage = () => {
  const { theme, isDarkMode } = useContext(ThemeContext);
  const { t } = useTranslation();
  
  // États pour les données dynamiques
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState({
    services: true,
    projects: true,
    solutions: true,
    testimonials: true,
    team: true
  });
  
  // Animation des statistiques
  const [statsRef, statsInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  // Récupération des données depuis l'API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Services (utilisez Featured si disponible, sinon utilisez getAll)
        try {
          let servicesData;
          try {
            // Essayer d'obtenir les services mis en avant
            const featuredRes = await serviceService.getFeatured();
            servicesData = featuredRes.data;
          } catch (error) {
            // Si pas de service mis en avant, prendre tous les services
            const allRes = await serviceService.getAll();
            servicesData = allRes.data;
          }
          setServices(servicesData);
        } catch (error) {
          console.error("Erreur lors du chargement des services:", error);
        } finally {
          setLoading(prev => ({ ...prev, services: false }));
        }
        
        // Projets
        try {
          const projectsRes = await projectService.getAll();
          setProjects(projectsRes.data);
        } catch (error) {
          console.error("Erreur lors du chargement des projets:", error);
        } finally {
          setLoading(prev => ({ ...prev, projects: false }));
        }
        
        // Solutions
        try {
          let solutionsData;
          try {
            // Essayer d'obtenir les solutions mises en avant
            const featuredRes = await solutionService.getFeatured();
            solutionsData = featuredRes.data;
          } catch (error) {
            // Si pas de solution mise en avant, prendre toutes les solutions
            const allRes = await solutionService.getAll();
            solutionsData = allRes.data;
          }
          setSolutions(solutionsData);
        } catch (error) {
          console.error("Erreur lors du chargement des solutions:", error);
        } finally {
          setLoading(prev => ({ ...prev, solutions: false }));
        }
        
        // Témoignages
        try {
          let testimonialsData;
          try {
            // Essayer d'obtenir les témoignages mis en avant
            const featuredRes = await testimonialService.getFeatured();
            testimonialsData = featuredRes.data;
          } catch (error) {
            // Si pas de témoignage mis en avant, prendre tous les témoignages
            const allRes = await testimonialService.getAll();
            testimonialsData = allRes.data;
          }
          setTestimonials(testimonialsData);
        } catch (error) {
          console.error("Erreur lors du chargement des témoignages:", error);
        } finally {
          setLoading(prev => ({ ...prev, testimonials: false }));
        }
        
        // Équipe (staff uniquement)
        try {
          // Récupération de tous les utilisateurs, filtrage côté client
          // Récupérer les membres de l'équipe (is_staff=true)
        const response = await userService.getAll({ is_staff: true });

        // Vérifier la structure de la réponse
        let members = [];
        if (response.data && Array.isArray(response.data.results)) {
          members = response.data.results;
        } else if (Array.isArray(response.data)) {
          members = response.data;
        }

        console.log("Team members data:", members);
        setTeamMembers(members);
 
        } catch (error) {
          console.error("Erreur lors du chargement de l'équipe:", error);
        } finally {
          setLoading(prev => ({ ...prev, team: false }));
        }
      } catch (error) {
        console.error("Erreur générale:", error);
        setLoading({
          services: false,
          projects: false,
          solutions: false,
          testimonials: false,
          team: false
        });
      }
    };
    
    fetchData();
  }, []);
  
  // Animations
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  return (
    <>
      <NeuralBackground/>
      
      {/* Hero Section */}
      <HeroSection>
        <HeroContainer>
          <HeroContent>
            <CompanyName
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>
                <span className="blue">Propenta</span>
                <span className="orange">Tech</span> <span className="gold">saving solutions</span>
              </h1>
            </CompanyName>
            
            <HeroDescription
              theme={theme}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <span style={{color: 'gold',  fontSize: '2.5rem', fontWeight: '600' }}>
               "
              </span>{' '}
              Transformer les défis en solutions numériques innovantes, en repoussant les limites de la créativité et de la pensée technologique.
              <span style={{ color: 'gold', fontSize: '2.5rem', fontWeight: '700' }}>
               "
              </span>
            </HeroDescription>

   
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}
            >
              <GradientButton
                variant="primary"
                size="large"
                to="/services"
              >
                Découvrir nos services
              </GradientButton>
              
              <Button
                variant="outline"
                size="large"
                to="/contact"
              >
                Nous contacter
              </Button>
              <FeaturedEvent theme={theme} />
            </motion.div>
          </HeroContent>
          
          <ImageContainer
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <img src={techImagePath} alt="PropentaTech" />
          </ImageContainer>
        </HeroContainer>
      </HeroSection>
      
      {/* Stats Section */}
      <StatsSection ref={statsRef} isDark={isDarkMode}>
        <StatsContainer>
          <StatItem
            initial={{ scale: 0.8, opacity: 0 }}
            animate={statsInView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StatNumber theme={theme}>3+</StatNumber>
            <StatLabel theme={theme}>Années d'expérience</StatLabel>
          </StatItem>
          
          <StatItem
            initial={{ scale: 0.8, opacity: 0 }}
            animate={statsInView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <StatNumber theme={theme}>18+</StatNumber>
            <StatLabel theme={theme}>Projets réalisés</StatLabel>
          </StatItem>
          
          <StatItem
            initial={{ scale: 0.8, opacity: 0 }}
            animate={statsInView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <StatNumber theme={theme}>9+</StatNumber>
            <StatLabel theme={theme}>Experts tech</StatLabel>
          </StatItem>
          
          <StatItem
            initial={{ scale: 0.8, opacity: 0 }}
            animate={statsInView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <StatNumber theme={theme}>15+</StatNumber>
            <StatLabel theme={theme}>Clients satisfaits</StatLabel>
          </StatItem>
        </StatsContainer>
      </StatsSection>
      
      {/* Services Section */}
      <Section>
        <Container>
          <SectionTitle
            title="Nos Services"
            subtitle="Des solutions innovantes pour répondre à vos besoins spécifiques"
          />
          
          {loading.services ? (
            <LoadingSpinner>
              <div className="spinner"></div>
            </LoadingSpinner>
          ) : (
            <>
              <Grid>
                {services.slice(0, 3).map((service, index) => (
                  <motion.div
                    key={service.id}
                    variants={fadeIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
                      image={service.image}
                      title={service.name}
                      description={service.short_description}
                      footer={
                        <Button
                          variant="outline"
                          size="small"
                          to={`/services/${service.id}`}
                        >
                          En savoir plus
                        </Button>
                      }
                    />
                  </motion.div>
                ))}
              </Grid>
              
              <motion.div
                style={{ textAlign: 'center', marginTop: '3rem' }}
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <GradientButton
                  variant="primary"
                  size="large"
                  to="/services"
                  theme={theme}
                >
                  Voir tous nos services
                </GradientButton>
              </motion.div>
            </>
          )}
        </Container>
      </Section>
      
      {/* Projects Section */}
      <Section style={{ background: isDarkMode ? 'rgba(13, 19, 33, 0.7)' : 'rgba(245, 248, 255, 0.7)' }}>
        <Container>
          <SectionTitle
            title="Projets Récents"
            subtitle="Découvrez comment nous avons aidé nos clients à réussir"
          />
          
          {loading.projects ? (
            <LoadingSpinner>
              <div className="spinner"></div>
            </LoadingSpinner>
          ) : (
            <>
              <Grid>
                {projects.slice(0, 3).map((project, index) => (
                  <motion.div
                    key={project.id}
                    variants={fadeIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
                      image={project.image}
                      title={project.title}
                      description={project.short_description}
                      footer={
                        <Button
                          variant="outline"
                          size="small"
                          to={`/projects/${project.id}`}
                        >
                          Voir le projet
                        </Button>
                      }
                    />
                  </motion.div>
                ))}
              </Grid>
              
              <motion.div
                style={{ textAlign: 'center', marginTop: '3rem' }}
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <GradientButton
                  variant="primary"
                  size="large"
                  to="/projects"
                  theme={theme}
                >
                  Explorer tous nos projets
                </GradientButton>
              </motion.div>
            </>
          )}
        </Container>
      </Section>
      
      
      
      {/* Solutions Section */}
      <Section style={{ background: isDarkMode ? 'rgba(13, 19, 33, 0.7)' : 'rgba(245, 248, 255, 0.7)' }}>
        <Container>
          <SectionTitle
            title="Nos Solutions"
            subtitle="Des produits innovants pour propulser votre entreprise"
          />
          
          {loading.solutions ? (
            <LoadingSpinner>
              <div className="spinner"></div>
            </LoadingSpinner>
          ) : (
            <SolutionsGrid>
              {solutions.slice(0, 4).map((solution, index) => (
                <motion.div
                  key={solution.id}
                  variants={fadeIn}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <SolutionCard theme={theme}>
                    <div className="image-container">
                      <img src={solution.image || '/assets/images/placeholder.jpg'} alt={solution.name} />
                    </div>
                    <div className="content">
                      <h3>{solution.name}</h3>
                      <p>{solution.short_description}</p>
                      <div className="technologies">
                        <small>{solution.technologies}</small>
                      </div>
                    </div>
                  </SolutionCard>
                </motion.div>
              ))}
            </SolutionsGrid>
          )}
          
          <motion.div
            style={{ textAlign: 'center', marginTop: '3rem' }}
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <GradientButton
              variant="primary"
              size="large"
              to="/solutions"
              theme={theme}
            >
              Découvrir toutes nos solutions
            </GradientButton>
          </motion.div>
        </Container>
      </Section>
      
      {/* Team Section */}
      <Section>
        <Container>
          <SectionTitle
            title="Notre Équipe"
            subtitle="Des experts passionnés à votre service"
          />
          
          {loading.team ? (
            <LoadingSpinner>
              <div className="spinner"></div>
            </LoadingSpinner>
          ) : (
            <TeamGrid>
              {Array.isArray(teamMembers) && teamMembers.slice(0, 8).map((member, index) => (
                  <motion.div
                    key={member.id}
                    variants={fadeIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                  <TeamMemberCard theme={theme}>
                    <div className="image-container">
                      <img 
                        src={member.profile_picture || '/assets/images/placeholder.jpg'} 
                        alt={`${member.first_name} ${member.last_name}`} 
                      />
                    </div>
                    <div className="content">
                      <h3>{member.first_name} {member.last_name}</h3>
                      <p>{member.position}</p>
                      <div className="social-links">
                        {member.linkedin_profile && (
                          <a href={member.linkedin_profile} target="_blank" rel="noopener noreferrer">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M22 3.09c0-.7-.57-1.27-1.27-1.27H3.27C2.57 1.82 2 2.39 2 3.09v17.82c0 .7.57 1.27 1.27 1.27h17.46c.7 0 1.27-.57 1.27-1.27V3.09zM7.22 19.28H4.74V9.87h2.48v9.41zM5.98 8.75c-.84 0-1.53-.69-1.53-1.53s.69-1.53 1.53-1.53 1.53.69 1.53 1.53-.69 1.53-1.53 1.53zm13.3 10.53h-2.48v-4.47c0-2.73-3.09-2.52-3.09 0v4.47h-2.48V9.87h2.48v1.49c1.08-2 5.57-2.15 5.57 1.91v5.91z" fill="currentColor"/>
                            </svg>
                          </a>
                        )}
                        {member.github_profile && (
                          <a href={member.github_profile} target="_blank" rel="noopener noreferrer">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.22.66-.48v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.52 2.34 1.08 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.8-.22 1.65-.33 2.5-.33.85 0 1.7.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0012 2z" fill="currentColor"/>
                            </svg>
                          </a>
                        )}
                        {member.twitter_profile && (
                          <a href={member.twitter_profile} target="_blank" rel="noopener noreferrer">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M22 5.8a8.49 8.49 0 01-2.36.64 4.13 4.13 0 001.81-2.27 8.21 8.21 0 01-2.61 1 4.1 4.1 0 00-7 3.74 11.64 11.64 0 01-8.45-4.29 4.16 4.16 0 001.27 5.49 4.09 4.09 0 01-1.86-.51v.05a4.1 4.1 0 003.29 4 4.1 4.1 0 01-1.85.07 4.11 4.11 0 003.83 2.85A8.22 8.22 0 012 18.28a11.57 11.57 0 006.29 1.85A11.59 11.59 0 0020 8.45v-.53a8.43 8.43 0 002-2.12z" fill="currentColor"/>
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  </TeamMemberCard>
                </motion.div>
              ))}
            </TeamGrid>
          )}
          
          <motion.div
            style={{ textAlign: 'center', marginTop: '3rem' }}
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <GradientButton
              variant="primary"
              size="large"
              to="/team"
              theme={theme}
            >
              Rencontrer toute l'équipe
            </GradientButton>
          </motion.div>
        </Container>
      </Section>
      {/* Testimonials Section */}
      <Section>
        <Container>
          <SectionTitle
            title="Témoignages Clients"
            subtitle="Ce que nos clients disent de notre collaboration"
          />
          
          {loading.testimonials ? (
            <LoadingSpinner>
              <div className="spinner"></div>
            </LoadingSpinner>
          ) : (
            <Grid>
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  variants={fadeIn}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <TestimonialCard theme={theme}>
                    <div className="quote">
                      {testimonial.testimonial}
                    </div>
                    <div className="author">
                      {/* <div className="author-image">
                        <img 
                          src={testimonial.image || '/assets/images/placeholder.jpg'} 
                          alt={testimonial.name} 
                        />
                      </div> */}
                      <div className="author-info">
                        <h4>{testimonial.name}</h4>
                        <p>{testimonial.position}{testimonial.company ? `, ${testimonial.company}` : ''}</p>
                      </div>
                    </div>
                  </TestimonialCard>
                </motion.div>
              ))}
            </Grid>
          )}
          
          <motion.div
            style={{ textAlign: 'center', marginTop: '3rem' }}
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Button
              variant="outline"
              size="large"
              to="/testimonials"
            >
              Voir tous les témoignages
            </Button>
          </motion.div>
        </Container>
      </Section>

      {/* Partners Section */}
      <PartnerSection theme={theme}>
        <Container>
          <SectionTitle
            title="Nos Partenaires"
            subtitle="Ils nous font confiance pour innover ensemble"
          />
                
          <PartnersGrid>
            {partners.map((partner, index) => (
              <motion.div
                key={partner.id}
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <PartnerLogo theme={theme}>
                  <img src={partner.logo} alt={partner.name} />
                </PartnerLogo>
              </motion.div>
            ))}
          </PartnersGrid>
        </Container>
      </PartnerSection>
    </>
  );
};

export default HomePage;